import { ZodSchema } from "@validation/utils/schemaUtils";
import { CraneType, LocationAvailabilityType } from "__generated__/enums";
import { intl } from "LocaleWrapper";
import { locationSelectOptionSchema } from "view/locations/utils/location.schema";
import z from "zod";

export enum AvailabilityRepeatType {
	None = "NONE",
	Interval = "INTERVAL",
	Days = "DAYS"
}

const customCraneSelectOptionSchema = z.object({
	id: z.number().nullish(),
	name: z.string().nullish(),
	craneType: z.enum(CraneType).nullish(),
});

const customBolderSelectOptionSchema = z.object({
	id: z.number().nullish(),
	description: z.string().nullish(),
});

export const availabilitySchema = z.object({
	id: z.number().nullable(),
	location: ZodSchema.requiredField(locationSelectOptionSchema),
	type: z.enum(LocationAvailabilityType),
	crane: customCraneSelectOptionSchema.nullable(),
	amountOfCranes: z.number().nullable(),
	craneSpeed: z.number().nullable(),
	fromDateTime: ZodSchema.requiredField(z.number()),
	toDateTime: ZodSchema.requiredField(z.number()),
	bolderFrom: customBolderSelectOptionSchema.nullable(),
	bolderTo: customBolderSelectOptionSchema.nullable(),
	maxWidthInMeters: z.number().nullable(),
	overflowLocations: z.array(locationSelectOptionSchema).nullable(),
	canHandleOOG: z.boolean(),
	canHandleBundles: z.boolean(),
	fullContainersAllowed: z.boolean(),
	twinningAllowed: z.boolean(),
	repeatType: z.enum(AvailabilityRepeatType).nullish(),
	repeatDayInterval: z.number().nullish(),
	endDate: z.number().nullish(),
	days: z.array(z.number()).nullish(),
}).superRefine((data, ctx) => {

	const isCrane = data.type === LocationAvailabilityType.CraneAvailability;
	const isQuay = data.type === LocationAvailabilityType.MooringAvailability;
	const isContainerCrane = data.crane?.craneType === CraneType.Container;
	const isCraneSpeedEnabled = isQuay || (isCrane && !data.crane) || isContainerCrane;

	const addError = (path: string) => ctx.addIssue({
		code: "custom",
		message: intl.formatMessage({ id: "ZOD_VALIDATION_REQUIRED" }),
		path: [ path ],
	});

	if (isCrane && !data.crane) {
		addError("crane");
	}

	if (isQuay && !data.amountOfCranes) {
		addError("amountOfCranes");
	}

	if (isCraneSpeedEnabled && !data.craneSpeed) {
		addError("craneSpeed");
	}

	if (!isCrane && !data.bolderFrom) {
		addError("bolderFrom");
	}

	if (!isCrane && !data.bolderTo) {
		addError("bolderTo");
	}

	if ((isQuay || isContainerCrane) && !data.maxWidthInMeters) {
		addError("maxWidthInMeters");
	}

	if ((data.repeatType === AvailabilityRepeatType.Interval) && !data.repeatDayInterval) {
		addError("repeatDayInterval");
	}

	if ((data.repeatType === AvailabilityRepeatType.Interval) && !data.endDate) {
		addError("endDate");
	}

	if ((data.repeatType === AvailabilityRepeatType.Days) && !data.days) {
		addError("days");
	}
}).transform((data) => {
	const isCrane = data.type === LocationAvailabilityType.CraneAvailability;
	const isQuay = data.type === LocationAvailabilityType.MooringAvailability;
	const isContainerCrane = data.crane?.craneType === CraneType.Container;
	const isCraneSpeedEnabled = isQuay || (isCrane && !data.crane) || isContainerCrane;

	return {
		id: data.id ?? undefined,
		type: data.type,
		location: {
			id: data.location.id,
		},
		crane: isCrane && data.crane ? { id: data.crane?.id } : undefined,
		amountOfCranes: isQuay && data.amountOfCranes ? data.amountOfCranes : undefined,
		craneSpeed: isCraneSpeedEnabled && data.craneSpeed ? data.craneSpeed : undefined,
		fromDateTime: data.fromDateTime,
		toDateTime: data.toDateTime,
		bolderFrom: !isCrane && data.bolderFrom ? { id: data.bolderFrom.id } : undefined,
		bolderTo: !isCrane && data.bolderTo ? { id: data.bolderTo.id } : undefined,
		maxWidthInMeters: (isQuay || isContainerCrane) && data.maxWidthInMeters ? data.maxWidthInMeters : undefined,
		overflowLocations: (isQuay || isContainerCrane) && data.overflowLocations ? data.overflowLocations.map(it => ({ id: it.id })) : undefined,
		canHandleOOG: (isQuay || isContainerCrane) ? data.canHandleOOG : false,
		canHandleBundles: (isQuay || isContainerCrane) ? data.canHandleBundles : false,
		fullContainersAllowed: (isQuay || isContainerCrane) ? data.fullContainersAllowed : false,
		twinningAllowed: (isQuay || isContainerCrane) ? data.twinningAllowed : false,
		repeatType: data.repeatType,
		repeatDayInterval: data.repeatDayInterval,
		endDate: data.endDate,
		days: data.days,
	};
});

export type AvailabilitySchemaInput = z.input<typeof availabilitySchema>;
export type AvailabilitySchemaOutput = z.output<typeof availabilitySchema>;
